import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const UPDATED = "June 2026";

const H = ({ children }) => (
  <h3 className="mt-8 font-display text-lg font-semibold tracking-tight text-white">{children}</h3>
);
const P = ({ children }) => (
  <p className="mt-3 text-sm leading-relaxed text-[#9aa2ac]">{children}</p>
);

const Terms = () => (
  <div>
    <P>Last updated: {UPDATED}</P>
    <P>
      These Terms &amp; Conditions ("Terms") govern your access to and use of the SÛRSWA AI
      website and services (the "Services"), operated by <strong className="text-white">Skydrobe Pty Ltd</strong>{" "}
      trading as SÛRSWA AI ("SÛRSWA AI", "we", "us" or "our"). By accessing this site or engaging
      our Services, you agree to these Terms.
    </P>

    <H>1. Services</H>
    <P>
      SÛRSWA AI designs and implements AI-powered systems and automation for businesses. Any
      proposal, scope, timeline or deliverable will be confirmed in a separate written engagement.
    </P>

    <H>2. Strategy Session &amp; payments</H>
    <P>
      The 60-minute AI Strategy Session is offered for AUD $298 (unless otherwise stated). Fees are
      payable in advance. Except where required by law, session fees are non-refundable.
    </P>

    <H>3. Intellectual property</H>
    <P>
      All content on this site — including text, branding, design, and code — is owned by or
      licensed to Skydrobe Pty Ltd and may not be copied or reused without written permission.
      Ownership of custom deliverables is governed by your individual engagement agreement.
    </P>

    <H>4. Acceptable use</H>
    <P>
      You agree not to misuse the Services, attempt unauthorised access, or use the site or our AI
      assistant for unlawful, harmful or abusive purposes.
    </P>

    <H>5. AI assistant</H>
    <P>
      Our website concierge is powered by third-party AI models and is provided for general
      information only. Responses may be inaccurate and do not constitute professional, legal or
      financial advice.
    </P>

    <H>6. Disclaimers &amp; limitation of liability</H>
    <P>
      The Services are provided "as is". To the maximum extent permitted by law, Skydrobe Pty Ltd
      excludes all implied warranties and is not liable for any indirect or consequential loss.
      Nothing in these Terms limits rights you have under the Australian Consumer Law.
    </P>

    <H>7. Governing law</H>
    <P>
      These Terms are governed by the laws of Australia, and you submit to the non-exclusive
      jurisdiction of its courts.
    </P>

    <H>8. Contact</H>
    <P>
      Questions about these Terms can be sent to Requests@surswa.com.
    </P>
    <P className="mt-6 text-[#6a7079]">
      Note: This is a template provided for convenience and should be reviewed by a qualified legal
      professional. Skydrobe Pty Ltd ABN and registered address to be inserted.
    </P>
  </div>
);

const Privacy = () => (
  <div>
    <P>Last updated: {UPDATED}</P>
    <P>
      This Privacy Policy explains how <strong className="text-white">Skydrobe Pty Ltd</strong>{" "}
      trading as SÛRSWA AI ("we", "us", "our") collects, uses and protects your personal
      information, in accordance with the Australian Privacy Principles under the Privacy Act 1988
      (Cth).
    </P>

    <H>1. Information we collect</H>
    <P>
      When you submit our booking or contact form we collect your name, email address, company and
      any message you provide. When you use our website concierge, we store the messages you send
      to support the conversation. We may also collect basic usage and device analytics.
    </P>

    <H>2. How we use it</H>
    <P>
      We use your information to respond to enquiries, arrange and deliver Strategy Sessions,
      provide and improve our Services, and communicate with you about your request.
    </P>

    <H>3. Third parties</H>
    <P>
      We use trusted service providers to operate the site — including an email delivery provider
      and third-party AI model providers that process concierge messages. These providers process
      data on our behalf and are not permitted to use it for their own purposes. We do not sell
      your personal information.
    </P>

    <H>4. Data retention &amp; security</H>
    <P>
      We retain personal information only as long as necessary for the purposes above or as
      required by law, and take reasonable steps to protect it from misuse, loss and unauthorised
      access.
    </P>

    <H>5. Your rights</H>
    <P>
      You may request access to, correction of, or deletion of your personal information by
      contacting us. You may also opt out of marketing communications at any time.
    </P>

    <H>6. Contact</H>
    <P>
      For privacy requests or complaints, contact us at Requests@surswa.com. If you are not
      satisfied with our response, you may contact the Office of the Australian Information
      Commissioner (OAIC).
    </P>
    <P className="mt-6 text-[#6a7079]">
      Note: This is a template provided for convenience and should be reviewed by a qualified legal
      professional. Skydrobe Pty Ltd ABN and registered address to be inserted.
    </P>
  </div>
);

export default function LegalModal({ type, onClose }) {
  const open = type === "terms" || type === "privacy";
  const title = type === "terms" ? "Terms & Conditions" : "Privacy Policy";
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] flex items-center justify-center p-4 md:p-6"
          data-testid="legal-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            className="glass relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-7 py-5">
              <h2 className="text-metal font-display text-xl font-semibold tracking-tight">{title}</h2>
              <button
                onClick={onClose}
                data-testid="legal-modal-close"
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#9aa2ac] transition-colors hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-y-auto px-7 py-6">
              {type === "terms" ? <Terms /> : <Privacy />}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
